import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';

import {
  getAllProjectsCustomFields,
  getWorkspaceCustomFieldsWithlabels,
} from '../../../../redux/workspace/selectors';
import './style.css';

const ITEM_HEIGHT = 42;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 15 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};
const FORM_STYLES = { m: 0, width: 300 };
const MENU_ITEM_STYLES = { padding: '0 5px' };
const MENU_SUBHEADER_ITEM_STYLES = {
  lineHeight: '42px',
  overflow: 'hidden',
  position: 'relative',
  textOverflow: 'ellipsis',
  textWrapMode: 'nowrap',
};

const CustomListSubheader = (props) => (
  <ListSubheader sx={MENU_SUBHEADER_ITEM_STYLES} {...props} />
);

CustomListSubheader.muiSkipListHighlight = true;

const AllCustomFieldsForExport = ({
  allCustomFieldsSelected,
  filters,
  projectsWithCustomFields,
  selectedCustomFields,
  setAllCustomFieldsSelected,
  setSelectedCustomFields,
  workspaceCustomFields,
}) => {
  const intl = useIntl();
  const [selected, setSelected] = useState([]);

  const projects = useMemo(() => filters?.projects || [], [filters?.projects]); // {_id: "ID"}

  // Filter projectsWithCustomFields to only include selected projects
  const filteredProjectsWithCustomFields = useMemo(() => {
    if (!projects || projects.length === 0) {
      return projectsWithCustomFields;
    }

    const projectIds = projects.map((p) => p._id);
    return projectsWithCustomFields.filter((project) =>
      projectIds.includes(project?.projectId),
    );
  }, [projectsWithCustomFields, projects]);

  const allCustomFieldsIdList = useMemo(
    () => [
      ...workspaceCustomFields.map((customField) => customField?.customFieldId),
      ...filteredProjectsWithCustomFields.reduce(
        (result, project) => [
          ...result,
          ...project?.customFields?.map(
            (customField) => customField?.customFieldId,
          ),
        ],
        [],
      ),
    ],
    [filteredProjectsWithCustomFields, workspaceCustomFields],
  );

  const handleChange = useCallback((event) => {
    const {
      target: { value },
    } = event;
    const newValue = typeof value === 'string' ? value.split(',') : value;
    setSelected(newValue);
    setSelectedCustomFields(newValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inputRenderer = useCallback(
    (selectedCustomFieldIds) =>
      selectedCustomFieldIds
        ?.map((selectedCustomFieldId) => {
          const customFieldName = workspaceCustomFields?.find(
            (customField) =>
              customField?.customFieldId === selectedCustomFieldId,
          )?.customFieldName;

          if (customFieldName) {
            return customFieldName;
          }

          for (let i = 0; i < filteredProjectsWithCustomFields?.length; i++) {
            for (
              let j = 0;
              j < filteredProjectsWithCustomFields[i]?.customFields?.length;
              j++
            ) {
              if (
                filteredProjectsWithCustomFields[i]?.customFields[j]
                  ?.customFieldId === selectedCustomFieldId
              ) {
                return filteredProjectsWithCustomFields[i]?.customFields[j]
                  ?.customFieldName;
              }
            }
          }
          return undefined;
        })
        .join(', '),
    [filteredProjectsWithCustomFields, workspaceCustomFields],
  );

  const projectFieldsOptionsRenderer = useMemo(
    () =>
      filteredProjectsWithCustomFields.reduce(
        (options, project) => [
          ...options,
          <CustomListSubheader key={project?.projectId}>
            <FormattedMessage id="PROJECT" defaultMessage="Project" />:{' '}
            {project?.projectName}
          </CustomListSubheader>,
          project?.customFields?.map((customField) => (
            <MenuItem
              key={customField?.customFieldId}
              value={customField?.customFieldId}
              sx={MENU_ITEM_STYLES}
            >
              <Checkbox
                checked={selected.includes(customField?.customFieldId)}
              />
              <ListItemText primary={customField?.customFieldName} />
            </MenuItem>
          )),
        ],
        [],
      ),
    [filteredProjectsWithCustomFields, selected],
  );

  const workspaceFieldsOptionsRenderer = useMemo(
    () => [
      <CustomListSubheader key="workspace-custom-fields-subheader">
        <FormattedMessage
          id="WORKSPACE_CUSTOM_FIELDS_SUBHEADER"
          defaultMessage="Workspace custom fields"
        />
        :
      </CustomListSubheader>,
      workspaceCustomFields?.map((customField) => (
        <MenuItem
          key={customField?.customFieldId}
          value={customField?.customFieldId}
          sx={MENU_ITEM_STYLES}
        >
          <Checkbox checked={selected.includes(customField?.customFieldId)} />
          <ListItemText primary={customField?.customFieldName} />
        </MenuItem>
      )),
    ],
    [workspaceCustomFields, selected],
  );

  useEffect(() => {
    if (allCustomFieldsSelected) {
      setSelected(allCustomFieldsIdList);
      setSelectedCustomFields(allCustomFieldsIdList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCustomFieldsSelected, allCustomFieldsIdList]);

  useEffect(() => {
    if (allCustomFieldsIdList?.length === selected?.length) {
      setAllCustomFieldsSelected(true);
    } else {
      setAllCustomFieldsSelected(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCustomFieldsIdList?.length, selected?.length]);

  useEffect(() => {
    if (isEmpty(selectedCustomFields)) {
      setSelected([]);
    }
  }, [selectedCustomFields]);

  return (
    <div className="all-custom-fields-for-export-container">
      <div className="all-custom-fields-for-export-label">Custom fields:</div>
      <FormControl sx={FORM_STYLES}>
        <InputLabel>{`${intl.formatMessage({ id: 'SELECTED_CUSTOM_FIELDS', defaultMessage: 'Selected custom fields' })}:`}</InputLabel>
        <Select
          multiple
          value={selected}
          onChange={handleChange}
          input={
            <OutlinedInput
              label={`${intl.formatMessage({ id: 'SELECTED_CUSTOM_FIELDS', defaultMessage: 'Selected custom fields' })}:`}
            />
          }
          renderValue={inputRenderer}
          MenuProps={MenuProps}
          sx={{
            '& .MuiList-root': {
              paddingTop: '10px',
            },
          }}
        >
          {workspaceFieldsOptionsRenderer}
          {projectFieldsOptionsRenderer}
        </Select>
      </FormControl>
    </div>
  );
};

const mapStateToProps = (state) => ({
  projectsWithCustomFields: getAllProjectsCustomFields(state),
  workspaceCustomFields: getWorkspaceCustomFieldsWithlabels(state),
});

export default connect(mapStateToProps)(AllCustomFieldsForExport);
